/**
 * Client-facing types for the shared game platform.
 *
 * These types mirror the API response models from the server,
 * but are defined here to avoid importing server-side code.
 */

export type { ApiResponse } from "../auth/types";

/**
 * Game room status
 */
export type GameRoomStatus =
  | "WAITING"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "ABANDONED";

/**
 * Single participant in a game room.
 * Server-provided readable identity so guest hash IDs never leak into the UI.
 */
export interface RoomParticipant {
  userId: string;
  displayName: string;
  isGuest: boolean;
}

/**
 * Game room (API response model)
 */
export interface GameRoom {
  id: string;
  gameType: string;
  status: GameRoomStatus;
  hostUserId: string;
  guestUserId: string | null;
  participantUserIds: string[];
  participants?: RoomParticipant[];
  currentPlayerCount: number;
  observerMode: boolean;
  observerSeatCount: number;
  observerUserIds: string[];
  observerParticipants?: RoomParticipant[];
  currentObserverCount: number;
  minPlayers: number;
  maxPlayers: number;
  config: Record<string, unknown>;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
}

/**
 * Game event (API response model)
 */
export interface GameEvent {
  id: string;
  gameId: string;
  eventType: string;
  sequenceNumber: number;
  userId: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

/**
 * Share link payload from POST /api/v2/game-platform/rooms/:roomId/share-link
 */
export interface ShareLink {
  roomId: string;
  gameType: string;
  roomNumber: string;
  secret: string;
  seat?: "PLAYER" | "OBSERVER";
  expiresAt: string;
  url: string;
}

/**
 * Share link resolution payload from GET /api/v2/game-platform/share-links/resolve
 */
export interface ResolvedShareLink {
  roomId: string;
  gameType: string;
  roomNumber: string;
  seat?: "PLAYER" | "OBSERVER";
  expiresAt: string;
}

/**
 * WebSocket client → server message types (game-ws-relay-service)
 */
export type WSClientMessage =
  | { type: "lobby:subscribe"; gameType: string }
  | { type: "lobby:unsubscribe" }
  | { type: "room:subscribe"; roomId: string }
  | { type: "room:unsubscribe" };

/**
 * WebSocket server → client message types (game-ws-relay-service)
 */
export type WSServerMessage =
  | { type: "connected"; userId: string }
  | { type: "lobby:games_updated"; games: GameRoom[] }
  | {
      type: "game:event";
      eventType: string;
      payload: Record<string, unknown>;
      seq: number;
      userId: string;
    }
  | { type: "game:room_updated"; room: GameRoom }
  // ADR-04: per-recipient projected ViewerState push for hidden-information
  // games (Celestia). Only the recipient's own WS connection receives it
  // (ws-relay `broadcastToUser`). Non-hidden games never emit this.
  | {
      type: "game:viewer_state";
      state: Record<string, unknown>;
      seq: number;
    }
  | { type: "error"; message: string };

const WS_SERVER_MESSAGE_TYPES: ReadonlySet<string> = new Set([
  "connected",
  "lobby:games_updated",
  "game:event",
  "game:room_updated",
  "game:viewer_state",
  "error",
]);

/**
 * Runtime type guard for WSServerMessage.
 * Validates that the value is an object with a known `type` field.
 */
export function isWSServerMessage(value: unknown): value is WSServerMessage {
  if (typeof value !== "object" || value === null) return false;
  if (!("type" in value)) return false;
  return (
    typeof value.type === "string" && WS_SERVER_MESSAGE_TYPES.has(value.type)
  );
}

/**
 * Player stats (API response model)
 */
export interface PlayerStats {
  userId: string;
  gamesPlayed: number;
  wins: number;
  losses: number;
  draws: number;
  recentGameIds: string[];
  winRate: number;
}
